import React, { useState, useEffect, useCallback } from 'react';
import { Globe, AlertCircle, CheckCircle2, Copy, RefreshCw, Trash2, ExternalLink, Info, Loader2 } from 'lucide-react';
import { 
  addCustomDomain, 
  removeCustomDomain, 
  getCustomDomainStatus, 
  verifyCustomDomain 
} from '../../../services/api';
import { usePenpal } from '../../../hooks/usePenpal';
import type { CustomDomainStatusResponse, VerificationRecord, DnsInstructions } from '../../../types';

interface HelpCenterCustomDomainProps {
  helpCenterId: string;
  isProCloudPlan: boolean;
  currentDomain?: string | null;
}

type DomainStatus = 'none' | 'pending' | 'verified' | 'error';

export const HelpCenterCustomDomain: React.FC<HelpCenterCustomDomainProps> = ({
  helpCenterId,
  isProCloudPlan,
  currentDomain: initialDomain
}) => {
  const { openExternalUrl } = usePenpal();
  const [domain, setDomain] = useState<string | null>(initialDomain || null);
  const [inputDomain, setInputDomain] = useState('');
  const [status, setStatus] = useState<DomainStatus>('none');
  const [verification, setVerification] = useState<VerificationRecord[]>([]);
  const [dnsInstructions, setDnsInstructions] = useState<DnsInstructions>({ cname: 'cname.vercel-dns.com', a: '76.76.21.21' });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch current domain status on mount and when domain changes
  const fetchDomainStatus = useCallback(async () => {
    if (!helpCenterId) return;
    
    try {
      const response: CustomDomainStatusResponse = await getCustomDomainStatus(helpCenterId);
      if (response.ok) {
        setDomain(response.customDomain);
        if (!response.customDomain) {
          setStatus('none');
        } else if (response.dnsConfigured) {
          setStatus('verified');
        } else {
          setStatus('pending');
          if (response.verification) {
            setVerification(response.verification);
          }
        }
        if (response.dnsInstructions) {
          setDnsInstructions(response.dnsInstructions);
        }
        if (response.error) {
          setError(response.error);
        }
      }
    } catch (err) {
      console.error('Error fetching domain status:', err);
    } finally {
      setIsLoadingStatus(false);
    }
  }, [helpCenterId]);

  useEffect(() => {
    if (isProCloudPlan) {
      setIsLoadingStatus(true);
      fetchDomainStatus();
    } else {
      setIsLoadingStatus(false);
    }
  }, [isProCloudPlan, fetchDomainStatus]);

  // Auto-poll when domain is pending (every 30 seconds)
  useEffect(() => {
    if (status !== 'pending' || !domain) return;
    
    const pollInterval = setInterval(() => {
      fetchDomainStatus();
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(pollInterval);
  }, [status, domain, fetchDomainStatus]);

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Add new domain
  const handleAddDomain = async () => {
    if (!inputDomain.trim()) {
      setError('Please enter a domain');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await addCustomDomain(helpCenterId, inputDomain.trim());
      if (response.ok) {
        setDomain(response.customDomain);
        // Use dnsConfigured to determine actual status
        setStatus(response.dnsConfigured ? 'verified' : 'pending');
        if (response.verification) {
          setVerification(response.verification);
        }
        // Store DNS instructions from Vercel
        if (response.dnsInstructions) {
          setDnsInstructions(response.dnsInstructions);
        }
        setInputDomain('');
      } else {
        setError((response as any).error || (response as any).errors?.[0] || 'Failed to add domain');
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || err.message || 'Failed to add domain');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove domain
  const handleRemoveDomain = async () => {
    if (!domain) return;
    
    if (!window.confirm(`Are you sure you want to remove ${domain}? Your help center will no longer be accessible at this address.`)) {
      return;
    }

    setIsRemoving(true);
    setError(null);

    try {
      const response = await removeCustomDomain(helpCenterId);
      if (response.ok) {
        setDomain(null);
        setStatus('none');
        setVerification([]);
      } else {
        setError((response as any).error || (response as any).errors?.[0] || 'Failed to remove domain');
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || err.message || 'Failed to remove domain');
    } finally {
      setIsRemoving(false);
    }
  };

  // Verify domain (check if DNS is configured)
  const handleVerifyDomain = async () => {
    if (!domain) return;

    setIsVerifying(true);
    setError(null);

    try {
      const response = await verifyCustomDomain(helpCenterId);
      if (response.ok) {
        // Use dnsConfigured to determine actual status
        setStatus(response.dnsConfigured ? 'verified' : 'pending');
        if (response.dnsConfigured) {
          setVerification([]);
        } else if (response.verification && response.verification.length > 0) {
          // Show verification records from Vercel if DNS not configured
          setVerification(response.verification);
        }
        // Update DNS instructions if provided
        if (response.dnsInstructions) {
          setDnsInstructions(response.dnsInstructions);
        }
      } else {
        setError((response as any).error || (response as any).errors?.[0] || 'Verification failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || err.message || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }

    // Refresh status after verification
    await fetchDomainStatus();
  };

  // Check if domain is apex (no subdomain)
  const isApexDomain = (d: string): boolean => {
    const parts = d.split('.');
    return parts.length === 2;
  };

  // Render upgrade prompt for non-pro users
  if (!isProCloudPlan) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Custom Domain</h3>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Use your own domain
              </h4>
              <p className="text-gray-600 mb-4">
                Connect a custom domain like <span className="font-mono text-sm bg-white px-2 py-0.5 rounded border">help.yourcompany.com</span> to 
                your help center for a fully branded experience.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mb-4">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  SSL certificate included automatically
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Works with subdomains and apex domains
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Easy DNS configuration
                </li>
              </ul>
              <a 
                href="/pricing" 
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Upgrade to Pro
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Globe className="w-5 h-5 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900">Custom Domain</h3>
      </div>

      {/* Help contact */}
      <p className="text-sm text-gray-500">
        Having trouble setting up your custom domain? Reach out to us at{' '}
        <button 
          onClick={() => openExternalUrl('mailto:hello@folge.me')}
          className="text-blue-600 hover:underline"
        >
          hello@folge.me
        </button>
        {' '}and we'll help you get it working.
      </p>

      {/* Loading initial domain status */}
      {isLoadingStatus && (
        <div className="flex items-center gap-3 py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin flex-shrink-0" />
          <span>Checking domain status...</span>
        </div>
      )}

      {/* Error display */}
      {!isLoadingStatus && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 text-sm font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* No domain configured */}
      {!isLoadingStatus && status === 'none' && (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Connect your own domain to serve your help center at a custom address like{' '}
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-800">help.yourcompany.com</span>
          </p>
          
          <div className="flex gap-3">
            <input
              type="text"
              value={inputDomain}
              onChange={(e) => setInputDomain(e.target.value)}
              placeholder="help.yourcompany.com"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <button
              onClick={handleAddDomain}
              disabled={isLoading || !inputDomain.trim()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Domain'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Domain configured - show status */}
      {!isLoadingStatus && (status === 'pending' || status === 'verified') && domain && (
        <div className="space-y-4">
          {/* Current domain card */}
          <div className={`border rounded-xl p-5 ${
            status === 'verified' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status === 'verified' ? (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900 font-mono">{domain}</p>
                  <p className={`text-sm ${status === 'verified' ? 'text-green-600' : 'text-amber-600'}`}>
                    {status === 'verified' ? 'Active - DNS configured correctly' : 'Waiting for DNS configuration'}
                  </p>
                  {status === 'pending' && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                      Auto-checking every 30s
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {status === 'pending' && (
                  <button
                    onClick={handleVerifyDomain}
                    disabled={isVerifying}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {isVerifying ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Check DNS
                  </button>
                )}
                <button
                  onClick={handleRemoveDomain}
                  disabled={isRemoving}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  {isRemoving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remove
                </button>
              </div>
            </div>
          </div>

          {/* DNS Instructions */}
          {status === 'pending' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Configure your DNS</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Add the following DNS record at your domain registrar (Cloudflare, GoDaddy, Namecheap, etc.) 
                    to point your domain to your help center.
                  </p>
                </div>
              </div>

              {/* Primary DNS record */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {isApexDomain(domain) ? 'A Record (Apex Domain)' : 'CNAME Record (Subdomain)'}
                  </span>
                </div>
                <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                  <span className="text-xs text-gray-500 uppercase">Type</span>
                  <code className="text-sm bg-white px-3 py-1.5 rounded border border-gray-200">
                    {isApexDomain(domain) ? 'A' : 'CNAME'}
                  </code>
                  <div className="w-8" />
                </div>
                <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                  <span className="text-xs text-gray-500 uppercase">Name</span>
                  <code className="text-sm bg-white px-3 py-1.5 rounded border border-gray-200">
                    {isApexDomain(domain) ? '@' : domain.split('.')[0]}
                  </code>
                  <button
                    onClick={() => copyToClipboard(isApexDomain(domain) ? '@' : domain.split('.')[0], 'name')}
                    className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    title="Copy"
                  >
                    {copiedField === 'name' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                  <span className="text-xs text-gray-500 uppercase">Value</span>
                  <code className="text-sm bg-white px-3 py-1.5 rounded border border-gray-200 truncate">
                    {isApexDomain(domain) ? dnsInstructions.a : dnsInstructions.cname}
                  </code>
                  <button
                    onClick={() => copyToClipboard(isApexDomain(domain) ? dnsInstructions.a : dnsInstructions.cname, 'value')}
                    className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    title="Copy"
                  >
                    {copiedField === 'value' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Verification records from Vercel (if any) */}
              {verification && verification.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    <strong>Ownership verification required:</strong> This domain is already in use elsewhere. 
                    Add the following DNS record to prove you own it:
                  </p>
                  {verification.map((record, index) => (
                    <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">{record.reason}</span>
                      </div>
                      <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                        <span className="text-xs text-gray-500 uppercase">Type</span>
                        <code className="text-sm bg-white px-3 py-1.5 rounded border border-amber-200">
                          {record.type}
                        </code>
                        <div className="w-8" />
                      </div>
                      <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                        <span className="text-xs text-gray-500 uppercase">Name</span>
                        <code className="text-sm bg-white px-3 py-1.5 rounded border border-amber-200 truncate">
                          {record.domain}
                        </code>
                        <button
                          onClick={() => copyToClipboard(record.domain, `domain-${index}`)}
                          className="p-1.5 hover:bg-amber-100 rounded transition-colors"
                          title="Copy"
                        >
                          {copiedField === `domain-${index}` ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <div className="grid grid-cols-[100px_1fr_auto] gap-3 items-center">
                        <span className="text-xs text-gray-500 uppercase">Value</span>
                        <code className="text-sm bg-white px-3 py-1.5 rounded border border-amber-200 truncate max-w-md">
                          {record.value}
                        </code>
                        <button
                          onClick={() => copyToClipboard(record.value, `value-${index}`)}
                          className="p-1.5 hover:bg-amber-100 rounded transition-colors"
                          title="Copy"
                        >
                          {copiedField === `value-${index}` ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-sm text-gray-500 space-y-2">
                <p>DNS changes can take up to 48 hours to propagate, but usually complete within a few minutes.</p>
                <p>
                  We'll automatically check every 30 seconds, or click <strong>"Check DNS"</strong> to verify manually.
                </p>
                <p>
                  Once DNS is configured, SSL certificate will be provisioned automatically (may take 1-2 minutes).
                </p>
                <p>
                  Check DNS propagation worldwide at{' '}
                  <button 
                    onClick={() => openExternalUrl(`https://www.whatsmydns.net/#CNAME/${domain}`)}
                    className="text-blue-600 hover:underline"
                  >
                    whatsmydns.net
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Success message when verified */}
          {status === 'verified' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Your custom domain is active</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Your help center is now accessible at{' '}
                    <button 
                      onClick={() => openExternalUrl(`https://${domain}`)}
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      https://{domain}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpCenterCustomDomain;
